import { DocTreeNode } from './doc-tree-node';
import { DocListEntry } from './doc-list-entry';
import { TreeNode } from 'primeng/api';
import { formatDate } from '@angular/common';

/**
 * Converts a flat, path-ordered DocTreeNode[] from the API into a
 * PrimeNG TreeNode hierarchy compatible with DocListEntry.
 *
 * @param flatNodes Path-ordered flat list from GET /DocumentList/tree/:docId.
 * @param activeDocId The DOCID the user navigated from (isEntryPoint node).
 * @returns Tuple: [rootNodes, selectedNode] where selectedNode is the
 *          PrimeNG TreeNode for the isEntryPoint row (for scroll-to/highlight).
 */
export function buildPrimeNgTree(
  flatNodes: DocTreeNode[],
  activeDocId: number
): [TreeNode<DocListEntry>[], TreeNode<DocListEntry> | null] {

  const map = new Map<number, TreeNode<DocListEntry>>();
  let selectedNode: TreeNode<DocListEntry> | null = null;

  // First pass: create all PrimeNG nodes
  for (const n of flatNodes) {
    // Convert DocTreeNode to DocListEntry-like structure for compatibility
    const docEntry: Partial<DocListEntry> = {
      DOCID: n.docId,
      PRIMDOCID: n.parentDocId,
      DOCTYPEID: n.docType,
      DOCDATE: n.docDate ? new Date(n.docDate) : undefined,
      DOCNUMBER: n.docNumber,
      DOCNAME: n.docName,
      CLIENTSNAME: n.clientsName
    };

    const treeNode: TreeNode<DocListEntry> = {
      label:    n.docName + ' ' + n.docNumber +
                ' від ' + (n.docDate ? formatDate(n.docDate, 'dd.MM.yyyy', 'en-US', 'GMT+02') : '') +
                '  (' + (n.clientsName || 'No client name') + ')',
      data:     docEntry as DocListEntry,
      expanded: true,
      children: [],
    };
    map.set(n.docId, treeNode);
    if (n.isEntryPoint || n.docId === activeDocId) {
      selectedNode = treeNode;
    }
  }

  // Second pass: wire parent references and collect roots
  const roots: TreeNode<DocListEntry>[] = [];
  for (const n of flatNodes) {
    const node = map.get(n.docId)!;
    if (n.parentDocId != null && map.has(n.parentDocId)) {
      const parent = map.get(n.parentDocId)!;
      node.parent = parent;
      parent.children!.push(node);
    } else {
      roots.push(node);
    }
  }

  // Expand all ancestors of the selected node
  if (selectedNode) {
    let pn: TreeNode | undefined = selectedNode;
    while (pn) { pn.expanded = true; pn = pn.parent; }
  }

  return [roots, selectedNode];
}
